<script>

import { onMount } from 'svelte';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { SAOPass } from 'three/examples/jsm/postprocessing/SAOPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

let container;
let camera, scene, renderer, controls;
let logo;
let starfieldMesh;
let uniforms = {
    iTime: { value: 0 },
    iResolution: { value: new THREE.Vector3(window.innerWidth, window.innerHeight, 5) },
    iMouse: { value: new THREE.Vector2() },
    viewerPosition: { value: new THREE.Vector3() },
    zoom: { value: 1.0 }
};

let circlingSpotlight1, circlingSpotlight2;
let composer, ssaoPass, saoPass;

onMount(() => {
  init();
  animate();
});

function init() {

  camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
  camera.position.set(0, 0, 1.5);

  scene = new THREE.Scene();
  scene.add(camera);
  scene.background = new THREE.Color(0x000000);

  const ambilight = new THREE.AmbientLight(0xffffff, 1);
  scene.add(ambilight);

  const light = new THREE.HemisphereLight(0xffffff, 0x444444, 1);
  light.position.set(0, -1, -1);
  scene.add(light);

  const spotLight = new THREE.SpotLight(0xffffff, 1);
  spotLight.position.set(0, 5, 5);
  spotLight.angle = Math.PI / 4;
  spotLight.penumbra = 0.1;
  spotLight.decay = 2;
  spotLight.distance = 50;
  scene.add(spotLight);

  const backLight = new THREE.SpotLight(0xffffff, 0.5);
  backLight.position.set(0, 5, -5);
  backLight.angle = Math.PI / 4;
  backLight.penumbra = 1.0;
  backLight.decay = 0;
  backLight.distance = 10;
  scene.add(backLight);

  circlingSpotlight1 = new THREE.SpotLight(0xffaaaa, 1);
  circlingSpotlight1.angle = Math.PI / 2;
  circlingSpotlight1.penumbra = 1.1;
  circlingSpotlight1.decay = 0.25;
  circlingSpotlight1.distance = 5;
  scene.add(circlingSpotlight1);

  circlingSpotlight2 = new THREE.SpotLight(0xc9c9ff, 2);
  circlingSpotlight2.angle = Math.PI / 6;
  circlingSpotlight2.penumbra = 1.2;
  circlingSpotlight2.decay = 0;
  circlingSpotlight2.distance = 7;
  scene.add(circlingSpotlight2);

  const starfieldMaterial = new THREE.ShaderMaterial({
    uniforms: uniforms,
    vertexShader: `
      void main() {
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `,
    fragmentShader: `
      precision mediump float;

      uniform vec2 iResolution;
      uniform vec2 iMouse;
      uniform float iTime;
      uniform vec3 viewerPosition;
      uniform float zoom;

      #define STARDUST_DEPTHS 10.
      #define STARDUST_DEPTH_SPEED 0.00001
      #define STARFIELD_QUANT 40.
      #define HOT_COLOR_MULT 1.2

      #define STAR_DEPTHS 5.
      #define STAR_DEPTH_SPEED 0.0000001

      vec3 nrand3(vec2 n) {
        vec3 a = fract( cos( n.x*8.3e-3 + n.y )*vec3(1.3e5, 4.7e5, 2.9e5) );
        vec3 b = fract( sin( n.x*0.3e-3 + n.y )*vec3(8.1e5, 1.0e5, 0.1e5) );
        vec3 c = mix(a, b, 0.5);
        return c;
      }

      float Hash21(vec2 p, float s1, float s2, float s3) {
        p = fract(p * vec2(s1, s2));
        p += dot(p, p + s3);
        return fract(p.x * p.y);	
      }

      vec3 starfieldRand(in vec2 uvs, in float depthLevel, in float spd) {
        vec3 p = vec3(uvs / 4., 0) + vec3(1., -1.3, 0.);
        p += viewerPosition.xyz * (depthLevel * spd);
        vec2 seed = p.xy * 2.0 + depthLevel;	
        seed = floor(seed * iResolution.x);
        return nrand3(seed);
      }

      vec3 HotColor(float size, float rand) {
        float hotnessRand = fract(rand * 56787.23);
        float colorRand = fract(rand * 12354.3);
        float hotness = hotnessRand * .5 + size * .5;
        
        vec3 color;
        if (hotness < .6) {
          color = vec3(1., hotness/.6 , hotness*.9);
        } else {
          float rg = 1. - (hotness - .6)/.4;
          color = vec3(rg,rg,1.);
        }
        color = clamp(color * HOT_COLOR_MULT, 0., 1.);
        return color;
      }

      vec4 Starfield(in float randNum) {
        vec4 starColor = vec4(pow(randNum, STARFIELD_QUANT));
        return starColor;
      }

      vec4 Stardust(in vec2 uvs) {
        vec4 c = vec4(0);
        for(float i=0.; i<1.; i +=1./STARDUST_DEPTHS) {
          vec3 r = starfieldRand(uvs, i, STARDUST_DEPTH_SPEED);
          c += Starfield(r.y + sin(iTime/8.0)*0.016);
        }
        c.a *= smoothstep(3., 1., zoom);
        return c;
      }

      vec4 Stars(in vec2 uvs) {
        vec4 c = vec4(0);
        for(float i=0.; i<1.; i +=1./STAR_DEPTHS) {
          vec3 r = starfieldRand(uvs, i, STAR_DEPTH_SPEED);
          c += Starfield(r.y);
          c *= vec4(HotColor(1., r.y), 1.);
        }
        return c;
      }

      void main() {
        vec2 fragCoord = gl_FragCoord.xy;
        vec2 uv = (fragCoord-.5*iResolution.xy)/iResolution.y;

        gl_FragColor += Stars(uv);
        gl_FragColor += Stardust(uv);

        if (gl_FragColor.a < .1 || (gl_FragColor.r < .1 && gl_FragColor.g < .1 && gl_FragColor.b < .1)) {
          discard;
        }
      }
    `,
    transparent: true
  });

  const starfieldGeometry = new THREE.PlaneGeometry(25, 25);
  starfieldMesh = new THREE.Mesh(starfieldGeometry, starfieldMaterial);

  starfieldMesh.position.z = -15;
  camera.add(starfieldMesh);

  const loader = new GLTFLoader();

  loader.load('logo.gltf', (gltf) => {
    logo = gltf.scene;
    logo.rotation.x = Math.PI / 2;
    logo.position.set(0, 0, 0);
    scene.add(logo);
  });

  renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.outputEncoding = THREE.sRGBEncoding

  renderer.setSize(window.innerWidth, window.innerHeight);
  container.appendChild(renderer.domElement);

  composer = new EffectComposer(renderer);
  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);

  saoPass = new SAOPass(scene, camera, false, true);
  saoPass.resolution.set(renderer.width, renderer.height);
  saoPass.params.output = SAOPass.OUTPUT.Default;
  saoPass.params.saoBias = 0.1;
  saoPass.params.saoIntensity = 0.08;
  saoPass.params.saoScale = 1028;
  saoPass.params.saoKernelRadius = 128;
  saoPass.params.saoMinResolution = 0;
  saoPass.params.saoBlur = true;
  saoPass.params.saoBlurRadius = 4;
  saoPass.params.saoBlurDepthCutoff = 0.1;

  let pixelRatio = renderer.getPixelRatio();


  composer.addPass(saoPass);
  const outputPass = new OutputPass();
  composer.addPass( outputPass );


  controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controls.dampingFactor = 0.05;
  controls.screenSpacePanning = false;
  controls.minDistance = 1;
  controls.maxDistance = 10;
  controls.maxPolarAngle = Math.PI;

  window.addEventListener('resize', onWindowResize);
}

function onWindowResize() {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  composer.setSize(window.innerWidth, window.innerHeight);
  uniforms.iResolution.value.set(window.innerWidth, window.innerHeight, 1);
}

function animate(time) {
  requestAnimationFrame(animate);
  uniforms.iTime.value = time * 0.001;
  uniforms.viewerPosition.value.copy(camera.position);
  uniforms.zoom.value = camera.zoom;
  
  const distance = 3;
  const speed = 0.001;
  
  circlingSpotlight1.position.x = Math.cos(time * speed) * distance;
  circlingSpotlight1.position.y = Math.sin(time * speed) * distance - 0.25;
  circlingSpotlight1.position.z = 2;
  
  circlingSpotlight2.position.x = Math.sin(time * speed + Math.PI) * distance + 0.25;
  circlingSpotlight2.position.y = Math.cos(time * speed + Math.PI) * distance - 0.5;
  circlingSpotlight2.position.z = 2;
  
  circlingSpotlight1.target.position.set(0, 0, 0);
  circlingSpotlight2.target.position.set(0, 0, 0);

  controls.update();
  composer.render();
}
</script>

<main bind:this={container}></main>

<style>
main {
  width: 100%;
  height: 100%;
}
</style>
